import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Settings,
  ChevronLeft,
  Calendar,
  Clock,
  Flag,
  Plus,
  Trash2,
  GripVertical,
  Copy,
  Save,
  Eye,
  CheckCircle,
  Timer,
  Car,
  ChevronDown,
  ChevronUp,
  Layers
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { useSeries } from '../../App';

// Mock event data
const mockEvent = {
  id: 123,
  name: 'Bristol Bash',
  date: '2024-03-15',
  track: 'Bristol Motor Speedway',
  status: 'setup',
};

// Race templates
const raceTemplates = [
  { id: 'heat', name: 'Heat Race', laps: 10, cars: 8, description: '4 heats, top 4 transfer' },
  { id: 'qualifier', name: 'Qualifier', laps: 12, cars: 12, description: 'Top 8 to feature redraw' },
  { id: 'b-main', name: 'B-Main', laps: 15, cars: 20, description: 'Top 4 transfer to A-Main' },
  { id: 'a-main', name: 'A-Main (Feature)', laps: 30, cars: 24, description: 'Full points race' },
  { id: 'lcs', name: 'Last Chance Showdown', laps: 12, cars: 16, description: 'Top 2 transfer' },
];

// Mock schedule
const initialSchedule = [
  { id: 1, type: 'hot-laps', name: 'Hot Laps', time: '5:00 PM', duration: 30, status: 'pending' },
  { id: 2, type: 'qualifying', name: 'Qualifying', time: '5:30 PM', duration: 45, status: 'pending' },
  { id: 3, type: 'heat', name: 'Heat 1', time: '6:30 PM', laps: 10, cars: 8, status: 'pending' },
  { id: 4, type: 'heat', name: 'Heat 2', time: '6:45 PM', laps: 10, cars: 8, status: 'pending' },
  { id: 5, type: 'heat', name: 'Heat 3', time: '7:00 PM', laps: 10, cars: 8, status: 'pending' },
  { id: 6, type: 'heat', name: 'Heat 4', time: '7:15 PM', laps: 10, cars: 8, status: 'pending' },
  { id: 7, type: 'b-main', name: 'B-Main', time: '7:45 PM', laps: 15, cars: 20, status: 'pending' },
  { id: 8, type: 'a-main', name: 'A-Main', time: '8:30 PM', laps: 30, cars: 24, status: 'pending' },
];

function ScheduleItem({ item, onEdit, onDelete, index }) {
  const [expanded, setExpanded] = useState(false);

  const typeColors = {
    'hot-laps': 'bg-slate-100 text-slate-600',
    'qualifying': 'bg-blue-100 text-blue-600',
    'heat': 'bg-amber-100 text-amber-600',
    'b-main': 'bg-orange-100 text-orange-600',
    'a-main': 'bg-emerald-100 text-emerald-600',
    'lcs': 'bg-purple-100 text-purple-600',
  };

  const statusIcons = {
    'pending': <Clock className="w-4 h-4 text-slate-400" />,
    'in_progress': <Timer className="w-4 h-4 text-blue-500 animate-pulse" />,
    'completed': <CheckCircle className="w-4 h-4 text-emerald-500" />,
  };

  return (
    <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
      <div className="flex items-center gap-3 p-3">
        <div className="cursor-grab text-slate-400 hover:text-slate-600">
          <GripVertical className="w-5 h-5" />
        </div>
        <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-sm font-medium text-slate-600">
          {index + 1}
        </div>
        <div className={`px-2 py-1 rounded text-xs font-medium ${typeColors[item.type] || 'bg-slate-100 text-slate-600'}`}>
          {item.type.replace('-', ' ').toUpperCase()}
        </div>
        <div className="flex-1">
          <div className="font-medium text-slate-800">{item.name}</div>
          <div className="text-xs text-slate-500">
            {item.time} {item.laps && `• ${item.laps} laps`} {item.cars && `• ${item.cars} cars`}
          </div>
        </div>
        {statusIcons[item.status]}
        <button
          onClick={() => setExpanded(!expanded)}
          className="p-1 text-slate-400 hover:text-slate-600"
        >
          {expanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
        </button>
        <button
          onClick={() => onDelete(item.id)}
          className="p-1 text-slate-400 hover:text-red-500"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
      {expanded && (
        <div className="px-4 pb-4 border-t border-slate-100 bg-slate-50">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-4">
            <div>
              <label className="block text-xs text-slate-500 mb-1">Start Time</label>
              <input
                type="time"
                defaultValue={item.time?.replace(' PM', '').replace(' AM', '')}
                className="w-full px-2 py-1 border border-slate-200 rounded text-sm"
              />
            </div>
            {item.laps !== undefined && (
              <div>
                <label className="block text-xs text-slate-500 mb-1">Laps</label>
                <input
                  type="number"
                  defaultValue={item.laps}
                  className="w-full px-2 py-1 border border-slate-200 rounded text-sm"
                />
              </div>
            )}
            {item.cars !== undefined && (
              <div>
                <label className="block text-xs text-slate-500 mb-1">Cars</label>
                <input
                  type="number"
                  defaultValue={item.cars}
                  className="w-full px-2 py-1 border border-slate-200 rounded text-sm"
                />
              </div>
            )}
            {item.duration !== undefined && (
              <div>
                <label className="block text-xs text-slate-500 mb-1">Duration (min)</label>
                <input
                  type="number"
                  defaultValue={item.duration}
                  className="w-full px-2 py-1 border border-slate-200 rounded text-sm"
                />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function RaceTemplateCard({ template, onAdd }) {
  return (
    <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg hover:border-rms-accent transition-colors">
      <div className="flex items-center justify-between mb-2">
        <div className="font-medium text-slate-800">{template.name}</div>
        <Button variant="ghost" size="sm" onClick={() => onAdd(template)}>
          <Plus className="w-4 h-4" />
        </Button>
      </div>
      <div className="text-xs text-slate-500">{template.description}</div>
      <div className="flex items-center gap-3 mt-2 text-xs text-slate-400">
        <span>{template.laps} laps</span>
        <span>{template.cars} cars</span>
      </div>
    </div>
  );
}

export default function EventSetup() {
  const { eventId } = useParams();
  const currentSeries = useSeries();
  const seriesSlug = currentSeries?.slug;

  const [schedule, setSchedule] = useState(initialSchedule);
  const [showTemplates, setShowTemplates] = useState(false);

  const handleAddTemplate = (template) => {
    const newItem = {
      id: Date.now(),
      type: template.id,
      name: template.name,
      time: 'TBD',
      laps: template.laps,
      cars: template.cars,
      status: 'pending',
    };
    setSchedule([...schedule, newItem]);
  };

  const handleDeleteItem = (id) => {
    setSchedule(schedule.filter(item => item.id !== id));
  };

  return (
    <div className="animate-fade-in">
      {/* Page Header */}
      <div className="page-header">
        <div className="flex items-center gap-4 mb-4">
          <Link to={`/${seriesSlug}/events/${eventId}`}>
            <Button variant="ghost" size="sm" leftIcon={ChevronLeft}>
              Back to Event
            </Button>
          </Link>
        </div>
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-violet-100 flex items-center justify-center">
              <Settings className="w-5 h-5 text-violet-600" />
            </div>
            <div>
              <h1 className="page-title">Event Setup</h1>
              <p className="page-subtitle">{mockEvent.name} - {mockEvent.track}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="secondary" leftIcon={Eye}>
              Preview
            </Button>
            <Button variant="primary" leftIcon={Save}>
              Save Setup
            </Button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Event Details */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-slate-500" />
                Event Details
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm text-slate-500 mb-1">Event Date</label>
                  <input
                    type="date"
                    defaultValue={mockEvent.date}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm text-slate-500 mb-1">Gates Open</label>
                  <input
                    type="time"
                    defaultValue="16:00"
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm text-slate-500 mb-1">Racing Starts</label>
                  <input
                    type="time"
                    defaultValue="18:30"
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Race Schedule */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Layers className="w-5 h-5 text-slate-500" />
                    Race Schedule
                  </CardTitle>
                  <CardDescription>Drag to reorder races</CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    leftIcon={Copy}
                  >
                    Copy from Template
                  </Button>
                  <Button
                    variant="primary"
                    size="sm"
                    leftIcon={Plus}
                    onClick={() => setShowTemplates(!showTemplates)}
                  >
                    Add Race
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {showTemplates && (
                <div className="mb-4 p-4 bg-slate-50 rounded-lg border border-slate-200">
                  <div className="text-sm font-medium text-slate-700 mb-3">Race Templates</div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {raceTemplates.map(template => (
                      <RaceTemplateCard
                        key={template.id}
                        template={template}
                        onAdd={handleAddTemplate}
                      />
                    ))}
                  </div>
                </div>
              )}
              <div className="space-y-2">
                {schedule.map((item, index) => (
                  <ScheduleItem
                    key={item.id}
                    item={item}
                    index={index}
                    onEdit={() => {}}
                    onDelete={handleDeleteItem}
                  />
                ))}
              </div>
              {schedule.length === 0 && (
                <div className="text-center py-8">
                  <Flag className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">No races scheduled yet</p>
                  <Button
                    variant="secondary"
                    size="sm"
                    className="mt-3"
                    leftIcon={Plus}
                    onClick={() => setShowTemplates(true)}
                  >
                    Add First Race
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Lineup Rules */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Car className="w-5 h-5 text-slate-500" />
                Lineup & Transfer Rules
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                  <div className="font-medium text-slate-700 mb-2">Heat Race Lineups</div>
                  <select className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm">
                    <option>Pill Draw</option>
                    <option>Points Invert</option>
                    <option>Random Draw</option>
                    <option>Qualifying Time</option>
                  </select>
                </div>
                <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                  <div className="font-medium text-slate-700 mb-2">Feature Lineup</div>
                  <select className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm">
                    <option>Heat Race Results</option>
                    <option>Qualifying Redraw</option>
                    <option>Points Invert</option>
                    <option>Owner Points</option>
                  </select>
                </div>
                <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                  <div className="font-medium text-slate-700 mb-2">Heat Transfers</div>
                  <input
                    type="number"
                    defaultValue={4}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                    placeholder="Top finishers that transfer"
                  />
                </div>
                <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                  <div className="font-medium text-slate-700 mb-2">B-Main Transfers</div>
                  <input
                    type="number"
                    defaultValue={4}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                    placeholder="Top finishers that transfer"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Setup Progress */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-slate-500" />
                Setup Progress
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-600">Event Details</span>
                  <CheckCircle className="w-4 h-4 text-emerald-500" />
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-600">Race Schedule</span>
                  <CheckCircle className="w-4 h-4 text-emerald-500" />
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-600">Lineup Rules</span>
                  <Clock className="w-4 h-4 text-amber-500" />
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-600">Payout Structure</span>
                  <Clock className="w-4 h-4 text-slate-300" />
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-600">Points Assignment</span>
                  <Clock className="w-4 h-4 text-slate-300" />
                </div>
              </div>
              <div className="mt-4 pt-4 border-t border-slate-200">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-slate-700">Overall Progress</span>
                  <span className="text-sm text-slate-500">60%</span>
                </div>
                <div className="w-full bg-slate-200 rounded-full h-2">
                  <div className="bg-rms-accent h-2 rounded-full" style={{ width: '60%' }} />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Quick Stats */}
          <Card>
            <CardHeader>
              <CardTitle>Event Summary</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center justify-between p-2 bg-slate-50 rounded">
                <span className="text-sm text-slate-600">Total Races</span>
                <span className="font-bold text-slate-800">{schedule.filter(s => s.type !== 'hot-laps' && s.type !== 'qualifying').length}</span>
              </div>
              <div className="flex items-center justify-between p-2 bg-slate-50 rounded">
                <span className="text-sm text-slate-600">Heat Races</span>
                <span className="font-bold text-slate-800">{schedule.filter(s => s.type === 'heat').length}</span>
              </div>
              <div className="flex items-center justify-between p-2 bg-slate-50 rounded">
                <span className="text-sm text-slate-600">Est. Duration</span>
                <span className="font-bold text-slate-800">3h 30m</span>
              </div>
              <div className="flex items-center justify-between p-2 bg-slate-50 rounded">
                <span className="text-sm text-slate-600">Feature Laps</span>
                <span className="font-bold text-slate-800">{schedule.find(s => s.type === 'a-main')?.laps || 0}</span>
              </div>
            </CardContent>
          </Card>

          {/* Templates */}
          <Card>
            <CardHeader>
              <CardTitle>Saved Templates</CardTitle>
              <CardDescription>Apply a pre-configured schedule</CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              <button className="w-full flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-200 hover:border-rms-accent transition-colors text-left">
                <div>
                  <div className="font-medium text-slate-700 text-sm">Standard Night</div>
                  <div className="text-xs text-slate-500">4 heats, B-Main, A-Main</div>
                </div>
                <ChevronLeft className="w-4 h-4 text-slate-400 rotate-180" />
              </button>
              <button className="w-full flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-200 hover:border-rms-accent transition-colors text-left">
                <div>
                  <div className="font-medium text-slate-700 text-sm">Big Show Format</div>
                  <div className="text-xs text-slate-500">6 heats, LCS, B-Main, A-Main</div>
                </div>
                <ChevronLeft className="w-4 h-4 text-slate-400 rotate-180" />
              </button>
              <button className="w-full flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-200 hover:border-rms-accent transition-colors text-left">
                <div>
                  <div className="font-medium text-slate-700 text-sm">Double Feature</div>
                  <div className="text-xs text-slate-500">Twin features format</div>
                </div>
                <ChevronLeft className="w-4 h-4 text-slate-400 rotate-180" />
              </button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
